import { useState } from 'react'
import AppDateRangePicker from '@/components/DatePickers/AppDateRangePicker'
import type { DateRangeValue } from '@/components/DatePickers/dateRange'
import { useTableStore } from '@/store/table/table'
import FilterCard from './FilterCard'
import { dateCodec, type DateValue } from './filterCodecs'
import type { FilterWidgetProps } from './filterKinds'

const pad = (n: number) => String(n).padStart(2, '0')

const toDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

const fromDay = (day?: string): Date | null => {
  if (!day) return null
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const validDate = (date: Date | null) => date !== null && !Number.isNaN(date.getTime())

/** The range as days. A lone "from" is that one day, as the old single-day filter was. */
const rangeToValue = ({ from, to }: DateRangeValue): DateValue => {
  const start = validDate(from) ? toDay(from as Date) : undefined
  const end = validDate(to) ? toDay(to as Date) : start
  return { from: start, to: end }
}

const DateRangeFilter = ({ field }: FilterWidgetProps) => {
  const { apiFilters, setApiFilters } = useTableStore()
  const stored = dateCodec.fromApi(apiFilters.filter(f => f.name === field))
  const [range, setRange] = useState<DateRangeValue>({
    from: fromDay(stored?.from),
    to: fromDay(stored?.to),
  })
  const [hasError, setHasError] = useState(false)

  const value = rangeToValue(range)
  const empty = !value.from && !value.to

  const apply = () => {
    const others = apiFilters.filter(f => f.name !== field)
    setApiFilters([...others, ...dateCodec.toApi(field, value)])
  }

  const clear = () => {
    setRange({ from: null, to: null })
    setApiFilters(apiFilters.filter(f => f.name !== field))
  }

  return (
    <FilterCard
      title="בחירת טווח תאריכים"
      isActive={stored !== null || !empty}
      onClear={clear}
      apply={{ disabled: empty || hasError, onApply: apply }}
    >
      <AppDateRangePicker
        value={range}
        onChange={setRange}
        onErrorChange={setHasError}
        width="100%"
      />
    </FilterCard>
  )
}

export default DateRangeFilter
